import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useBlocker } from "react-router-dom";
import { UnsavedChangesContext } from "@/hooks/useUnsavedChanges";
import { ConfirmDialog } from "./ConfirmDialog";

export function UnsavedChangesProvider({
  children,
  canBlock = () => true,
}: {
  children: ReactNode;
  canBlock?: () => boolean;
}) {
  const [dirtyForms, setDirtyForms] = useState<Set<string>>(() => new Set());
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const dirty = dirtyForms.size > 0;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      canBlock() &&
      nextLocation.pathname !== "/login" &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search),
  );
  const setDirty = useCallback((id: string, value: boolean) => {
    setDirtyForms((previous) => {
      if (previous.has(id) === value) return previous;
      const next = new Set(previous);
      if (value) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const requestDiscard = useCallback(
    (action: () => void) => {
      if (dirty) setPendingAction(() => action);
      else action();
    },
    [dirty],
  );
  const value = useMemo(
    () => ({ setDirty, requestDiscard }),
    [setDirty, requestDiscard],
  );

  useEffect(() => {
    if (!dirty) return;
    const preventLoss = (event: BeforeUnloadEvent) => {
      if (!canBlock()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", preventLoss);
    return () => window.removeEventListener("beforeunload", preventLoss);
  }, [dirty, canBlock]);

  const stay = () => {
    setPendingAction(null);
    if (blocker.state === "blocked") blocker.reset();
  };
  const discard = () => {
    setPendingAction(null);
    if (pendingAction) pendingAction();
    else if (blocker.state === "blocked") blocker.proceed();
  };

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={Boolean(pendingAction) || blocker.state === "blocked"}
        title="Discard unsaved changes?"
        message="Your emission entry has not been saved. Stay to keep working, or discard it to continue."
        confirmLabel="Discard and continue"
        cancelLabel="Stay on this page"
        confirmColor="error"
        onConfirm={discard}
        onClose={stay}
      />
    </UnsavedChangesContext.Provider>
  );
}
